import { useState, useEffect, useRef } from "react";
import { collectionGroup, query, where, onSnapshot } from "firebase/firestore";
import { firestoreDb } from "@/lib/firebase";

export type OrderStatusCode = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface FirebaseNotification {
  id: string;
  orderId: string;
  orderNumber: string;
  status: OrderStatusCode;
  message: string;
  messageAr: string;
  time: string;
  timeAr: string;
  read: boolean;
  timestamp: number; // ms, for sorting
}

const STATUS_LABEL_EN: Record<OrderStatusCode, string> = {
  0: "Order placed",
  1: "Order rejected",
  2: "Order accepted",
  3: "Your order is being prepared",
  4: "Your order is ready",
  5: "Your order is on the way",
  6: "Order delivered",
  7: "Order pending",
  8: "Handed to driver",
};

const STATUS_LABEL_AR: Record<OrderStatusCode, string> = {
  0: "تم استلام الطلب",
  1: "تم رفض الطلب",
  2: "تم قبول الطلب",
  3: "جاري تحضير طلبك",
  4: "طلبك جاهز",
  5: "طلبك في الطريق إليك",
  6: "تم توصيل الطلب",
  7: "الطلب قيد الانتظار",
  8: "تم تسليم الطلب للسائق",
};

function parseStatus(value: unknown): OrderStatusCode {
  const n = typeof value === "number" ? value : parseInt(String(value ?? "7"), 10);
  return n >= 0 && n <= 8 ? (n as OrderStatusCode) : 7;
}

/**
 * Safely parse any date value coming from Firestore:
 * - Firestore Timestamp object  { seconds, nanoseconds }
 * - ISO string with offset      "2026-03-26T13:02:59+02:00"
 * - Plain ISO string            "2026-03-26T11:02:59Z"
 * - Epoch number
 * Returns a ms timestamp, or Date.now() as fallback.
 */
function safeParseDateMs(value: unknown): number {
  if (!value) return Date.now();

  // Firestore Timestamp object
  if (typeof value === "object" && value !== null && "seconds" in value) {
    return (value as { seconds: number }).seconds * 1000;
  }

  // Number (epoch ms or seconds)
  if (typeof value === "number") {
    // If it looks like epoch seconds (< year 3000 in seconds)
    return value > 1e10 ? value : value * 1000;
  }

  if (typeof value === "string") {
    // Replace timezone offset like "+02:00" — Date.parse handles it,
    // but normalise just in case the environment chokes on it
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.getTime();

    // Last resort: strip the timezone offset and parse as UTC
    const stripped = value.replace(/[+-]\d{2}:\d{2}$/, "Z");
    const d2 = new Date(stripped);
    if (!isNaN(d2.getTime())) return d2.getTime();
  }

  return Date.now();
}

function formatRelativeTime(ms: number): { en: string; ar: string } {
  const diff = Math.floor((Date.now() - ms) / 60000);
  if (diff < 1) return { en: "Just now", ar: "الآن" };
  if (diff < 60) return { en: `${diff} min ago`, ar: `قبل ${diff} دقيقة` };
  const h = Math.floor(diff / 60);
  return { en: `${h}h ago`, ar: `قبل ${h} ساعة` };
}

const READ_STORAGE_KEY = "bb_notif_read";

function getReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<string>) {
  try {
    localStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...ids]));
  } catch { /* ignore */ }
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export function useFirebaseNotifications(mobile: string | null | undefined,projectId: string | null | undefined) {
  mobile = mobile?.replace("+","") ?? null;
  const [notifications, setNotifications] = useState<FirebaseNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const readIdsRef = useRef<Set<string>>(getReadIds());

  const markAllRead = () => {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, read: true }));
      const ids = new Set(updated.map((n) => n.id));
      readIdsRef.current = ids;
      saveReadIds(ids);
      return updated;
    });
  };

  const markRead = (id: string) => {
    readIdsRef.current.add(id);
    saveReadIds(readIdsRef.current);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  useEffect(() => {
    if (!mobile || !projectId) {
      setNotifications([]);
      return;
    }

    setLoading(true);

    // Only filter by phoneNumber — createdAt is stored as a string in this
    // backend, so we can't use a Firestore Timestamp range query on it.
    // We filter the 24h window client-side after parsing the date.
    const q = query(
      collectionGroup(firestoreDb, "orders"),
      where("phoneNumber", "==", mobile),
      where("projectId", "==", projectId)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items: FirebaseNotification[] = [];
        const readIds = readIdsRef.current;
        const cutoffMs = Date.now() - TWENTY_FOUR_HOURS_MS;

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;

          // Client-side 24h filter
          const orderCreatedMs = safeParseDateMs(
            data.createdAt ?? data.created_at ?? data.placedAt ?? data.placed_at
          );
          if (orderCreatedMs < cutoffMs) return;

          const orderId = docSnap.id;
          const rawOrderNumber =
            (data.orderId as string) ||
            (data.orderNumber as string) ||
            (data.order_number as string) ||
            "";
          const orderNumber = rawOrderNumber.replace(/^#/, "");

          const log = Array.isArray(data.log) ? data.log : [];

          log.forEach(
            (entry: { status?: unknown; date?: unknown }, idx: number) => {
              const entryMs = safeParseDateMs(entry.date);
              const status = parseStatus(entry.status);
              const id = `${orderId}_${idx}`;
              const { en, ar } = formatRelativeTime(entryMs);
              const orderLabel = orderNumber ? ` #${orderNumber}` : "";

              items.push({
                id,
                orderId,
                orderNumber,
                status,
                message: `${STATUS_LABEL_EN[status]}${orderLabel}`,
                messageAr: `${STATUS_LABEL_AR[status]}${orderLabel}`,
                time: en,
                timeAr: ar,
                read: readIds.has(id),
                timestamp: entryMs,
              });
            }
          );
        });

        // Sort newest log entry first
        items.sort((a, b) => b.timestamp - a.timestamp);

        setNotifications(items);
        setLoading(false);
      },
      (err) => {
        console.error("[FirebaseNotifications] Listener error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [mobile]);

  return { notifications, loading, markRead, markAllRead };
}